'use strict';

require('./CarouselBin-style.scss');
const Backbone = require('dibs-backbone');
const { carouselBinTemplate: template } = require('./CarouselBin-template');

/**
 * Represents the area of an image carousel that displays additional images that can be viewed. This can be hooked into
 * a full carousel component by passing in callbacks that will be called whenever an image changes. There are some quirks
 * regarding the number of images shown at a given time. By default, 5 are shown and the CSS is setup to make this work.
 * If a custom number is passed, the CSS must also be overridden to ensure the correct number are shown. Ideally this
 * would be driven entirely by either JS or CSS instead.
 *
 * @param  {array} options.images An array of images to display in the view. Images should have a `src` attribute containing the location of the image.
 * @param  {boolean} [options.numberVisible=5] The number of images to show at any one time.
 * @param  {boolean} [options.jumpToImageCallback] Callback that fires when an image in the bin is clicked on directly.
 * @param  {boolean} [options.updateImageCallback] Callback that fires whenever the image is updated.
 * @return {object}
 */
const CarouselBin = Backbone.BaseView.extend({

    events: {
        'click .carousel-bin-image-wrapper': '_jumpToImage'
    },

    initialize(options = {}) {
        this._images = options.images || [];
        this._numberVisible = options.numberVisible || 5;
        this._jumpToImageCallback = options.jumpToImageCallback;
        this._updateImageCallback = options.updateImageCallback;

        this._selected = 0;
        // Keeps track of the index of the leftmost visible image.
        this._leftIndex = 0;
        this._offset = 0;
    },

    render() {
        this.setElement(template({
            images: this._images.map((image) => {
                return image.src;
            })
        }));

        this.$images = this.$('.carousel-bin-image-wrapper');

        if (this.$images.length === 1) {
            this.$images.addClass('is-invisible');
        }

        return this;
    },

    /**
     * Move to the next image.
     * @param  {function} callback Callback that will be passed the index of the new image.
     */
    next(callback) {
        const imagesLength = this._images.length;
        let newSelected = this._selected + 1;

        // Keep us within range.
        if (newSelected >= imagesLength) {
            newSelected = 0;
            this._leftIndex = 0;

        // Detrmines whether or not the bin should scroll to the left, revealing a new image on the right and hiding one
        // on the left.
        } else if ((this._leftIndex + this._numberVisible - 1) < (imagesLength - 1)) {
            this._leftIndex += 1;
        }

        this._setSelected(newSelected, callback);
        this._toggleSelectedClass();
        this._translatePosition();
    },

    /**
     * Move to the previous image.
     * @param  {function} callback Callback that will be passed the index of the new image.
     */
    previous(callback) {
        const lastIndex = this._images.length - 1;
        let newSelected = this._selected - 1;

        // Keep us within range.
        if (newSelected < 0) {
            newSelected = lastIndex;
            this._leftIndex = Math.max(0, lastIndex - (this._numberVisible - 1));

        // Detrmines whether or not the bin should scroll to the right, revealing a new image on the left and hiding one
        // on the right.
        } else if (this._leftIndex > 0) {
            this._leftIndex -= 1;
        }

        this._setSelected(newSelected, callback);
        this._toggleSelectedClass();
        this._translatePosition();
    },

    /**
     * Directly set the selected image and the leftmost visible image. Useful for if the view state needs to be
     * updated based on something happening elsewhere on the page.
     * @param {number} selected  New image to select.
     * @param {number} leftIndex  Leftmost visible image.
     */
    setPosition(selected, leftIndex) {
        // If nothing is passed in, jsut keep the existing state.
        selected = typeof selected !== 'undefined' ? selected : this._selected;
        leftIndex = typeof leftIndex !== 'undefined' ? leftIndex : this._leftIndex;

        this._leftIndex = leftIndex;

        this._setSelected(selected);
        this._translatePosition();
        this._toggleSelectedClass();
    },

    /**
     * Returns the index of the selected image.
     * @return {number}
     */
    getSelected() {
        return this._selected;
    },

    /**
     * Updates the selected image from clicking directly on the bin.
     * @param  {object} e
     */
    _jumpToImage(e) {
        const target = e.currentTarget;
        // Fix for IE10 not supporting `dataset`.
        const index = target.dataset ? target.dataset.index : target.getAttribute('data-index');

        // The index is added as a data attribute during rendering so we can just grab it here.
        this._setSelected(Number(index), this._jumpToImageCallback);
        this._toggleSelectedClass();
    },

    /**
     * Helper method to get the width of an image. It is caches after the first call. This assumed the images don't
     * change size after the initial open. Ideally images should be resizable and this should get the new size.
     * @return {number} Image width.
     */
    _getImageWidth() {
        if (!this._imageWidth) {
            this._imageWidth = this.$images.get(0).clientWidth;
        }

        return this._imageWidth;
    },

    /**
     * Adds transformation to the images in the bin to give them a sliding effect.
     */
    _translatePosition() {
        this._offset = this._getImageWidth() * -this._leftIndex;

        // JQuery adds the proper vendor prefixes automatically.
        this.$images.css({ transform: 'translate(' + this._offset + 'px)' });
    },

    /**
     * Sets the `selected` class on the newly selected image.
     */
    _toggleSelectedClass() {
        if (this.$selected) {
            this.$selected.removeClass('selected');
        }

        this.$selected = this.$('[data-index="' + this._selected + '"]');
        this.$selected.addClass('selected');
    },

    /**
     * Actually sets the newly selected image and calls the callbacks.
     * @param {number}   newSelected
     * @param {function} callback Callback only called if the new image is different from the currently selected image.
     */
    _setSelected(newSelected, callback) {
        if (this._selected === newSelected) {
            return;
        }

        this._selected = newSelected;

        if (typeof this._updateImageCallback === 'function') {
            this._updateImageCallback(this._selected);
        }

        if (typeof callback === 'function') {
            callback(this._selected);
        }
    }

});

module.exports = CarouselBin;



// WEBPACK FOOTER //
// ./assets/js/components/CarouselBin/CarouselBin-view.es.js